// src/router/routesConfig.ts
import Dashboard from "@src/presentation/pages/dashboard/Dashboard";
import AllPascosPage from "@src/presentation/pages/pascos/AllPascosPage";
import CreatePascoPage from "@src/presentation/pages/pascos/CreatePascoPage";
import EditPascoPage from "@src/presentation/pages/pascos/EditPascoPage";
import PendingApprovalPage from "@src/presentation/pages/pascos/PendingApprovalPage";
import PendingReviewPage from "@src/presentation/pages/pascos/PendingReviewPage";
import ProcessedPascosPage from "@src/presentation/pages/pascos/ProcessedPascosPage";
import PascoDetailPage from "@src/presentation/pages/pascos/PascoDetailPage";
import AllUsersPage from "@src/presentation/pages/users/AllUsersPage";
import UserDetailPage from "@src/presentation/pages/users/UserDetailPage";
import CreateAdminPage from "@src/presentation/pages/users/CreateAdminPage";
import EditUserPage from "@src/presentation/pages/users/EditUserPage";
import RolesPage from "@src/presentation/pages/rbac/RolesPage";
import PermissionsPage from "@src/presentation/pages/rbac/PermissionsPage";
import AllUniversitiesPage from "@src/presentation/pages/universities/AllUniversitiesPage";
import CreateUniversityPage from "@src/presentation/pages/universities/CreateUniversityPage";
import UniversityDetailPage from "@src/presentation/pages/universities/UniversityDetailPage";
import EditUniversityPage from "@src/presentation/pages/universities/EditUniversityPage";
import AllTasksPage from "@src/presentation/pages/tasks/AllTasksPage";
import FailedTasksPage from "@src/presentation/pages/tasks/FailedTasksPage";
import TaskDetailPage from "@src/presentation/pages/tasks/TaskDetailPage";
import ConfigurationsPage from "@src/presentation/pages/system/ConfigurationsPage";
import SystemHealthPage from "@src/presentation/pages/system/SystemHealthPage";
import ProfilePage from "@src/presentation/pages/account/ProfilePage";
import SecurityPage from "@src/presentation/pages/account/SecurityPage";
import NotificationsPage from "@src/presentation/pages/notifications/NotificationsPage";
import HelpCenterPage from "@src/presentation/pages/help/HelpCenterPage";
import { LoginPage } from "@src/presentation/pages/auth";
import AdminLoginPage from "@src/presentation/pages/auth/AdminLoginPage";
import { OnboardingUniversityPage, OnboardingNamePage } from "@src/presentation/pages/onboarding";
import PublicHomePageRevamped from "@src/presentation/pages/public/PublicHomePageRevamped";
import PublicPascoDetailPage from "@src/presentation/pages/public/PublicPascoDetailPage";
import { ChartGantt, Globe, Proportions } from "lucide-react";
import PublicUploadPascoPage from "@src/presentation/pages/public/PublicUploadPascoPage";
import WhatsAppLinkPage from "@src/presentation/pages/account/WhatsAppLinkPage";
import { PERMISSIONS } from "@src/shared/constants/permissions";
import type { Permission } from "@src/shared/constants/permissions";

import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

// Strategy pattern: Component that renders different implementations based on site type
const ConditionalComponent = ({ adminComponent, publicComponent }: { adminComponent: ReactNode; publicComponent: ReactNode }) => {
  const isAdminSite = import.meta.env.VITE_IS_ADMIN_SITE === 'true';
  return isAdminSite ? adminComponent : publicComponent;
};

export type AppRoute = {
  id: string;
  path: string;
  element?: ReactNode;
  label?: string;
  icon?: ReactNode;
  isDisabled?: boolean;
  protected: boolean;
  isPublic?: boolean; // Public routes accessible without authentication
  isOnboarding?: boolean; // Onboarding routes (authenticated but profile incomplete)
  isHome?: boolean;
  /** Single permission required to access route */
  permission?: Permission | string;
  /** Array of permissions - user must have ALL to access route */
  requiredPermissions?: (Permission | string)[];
  /** Array of permissions - user must have ANY ONE to access route */
  anyPermissions?: (Permission | string)[];
};

export const routesConfig: AppRoute[] = [
  // ==================== PUBLIC ROUTES ====================
  // Public Homepage - Browse PASCOs (conditional: admin redirects to dashboard, public shows homepage)
  {
    id: "home",
    path: "/",
    element: <ConditionalComponent adminComponent={<Navigate to="/dashboard" replace />} publicComponent={<PublicHomePageRevamped />} />,
    label: "Home",
    protected: false,
    isPublic: true,
    isHome: true,
    isDisabled: import.meta.env.VITE_IS_ADMIN_SITE !== 'true', //
  },
  // Public PASCO Detail - Accessible to all users (conditional: admin or public detail page)
  {
    id: "pasco-detail",
    path: "/pascos/:id",
    element: <ConditionalComponent adminComponent={<PascoDetailPage />} publicComponent={<PublicPascoDetailPage />} />,
    label: "PASCO Details",
    protected: false,
    isPublic: true,
    isDisabled: import.meta.env.VITE_IS_ADMIN_SITE !== 'true', // Disable on admin site since we have a different detail page
  },
  // Upload PASCO - Allow authenticated users to upload PASCOs
  {
    id: "upload-pasco",
    path: "/upload",
    element: <PublicUploadPascoPage />,
    label: "Upload PASCO",
    protected: false,
    isPublic: true,
  },

  // ==================== AUTH ROUTES ====================
  // Auth (conditional: admin or public)
  {
    id: "auth",
    path: "/auth",
    element: <ConditionalComponent adminComponent={<AdminLoginPage />} publicComponent={<LoginPage />} />,
    label: "Login",
    protected: false,
  },

  // ==================== ONBOARDING ROUTES ====================
  {
    id: "onboarding-university",
    path: "/onboarding/university",
    element: <OnboardingUniversityPage />,
    label: "Select University",
    protected: false,
    isOnboarding: true,
  },
  {
    id: "onboarding-name",
    path: "/onboarding/name",
    element: <OnboardingNamePage />,
    label: "Set Name",
    protected: false,
    isOnboarding: true,
  },

  // ==================== DASHBOARD ROUTES ====================
  // Dashboard (conditional: admin dashboard or public redirect)
  {
    id: "dashboard",
    path: "/dashboard",
    element: <ConditionalComponent adminComponent={<Dashboard />} publicComponent={<PublicHomePageRevamped />} />,
    label: "Dashboard",
    icon: <Proportions />,
    protected: true,
    // No permissions required - components inside handle their own permission checks
  },

  // ==================== ADMIN FEATURES ====================
  // Analytics (admin only)
  {
    id: "analytics",
    path: "/analytics",
    element: <ConditionalComponent adminComponent={<div>Analytics Page</div>} publicComponent={<PublicHomePageRevamped />} />,
    label: "Analytics",
    icon: <ChartGantt size={16} />,
    protected: true,
    permission: PERMISSIONS.STATS_READ,
  },
  // Integrations (admin only)
  {
    id: 'integrations',
    path: '/integrations',
    element: <ConditionalComponent adminComponent={<div>Integrations Page</div>} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Integrations',
    icon: <Globe size={16} />,
    protected: true,
    // No specific permission - available to all authenticated users
  },

  // Pascos Routes (admin management)
  {
    id: 'pascos',
    path: '/pascos',
    element: <ConditionalComponent adminComponent={<AllPascosPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'All Pascos',
    protected: true,
    anyPermissions: [PERMISSIONS.PASCO_READ, PERMISSIONS.PASCO_SEARCH],
  },
  {
    id: 'pascos-new',
    path: '/pascos/new',
    element: <ConditionalComponent adminComponent={<CreatePascoPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Create Pasco',
    protected: true,
    permission: PERMISSIONS.PASCO_UPLOAD,
  },
  {
    id: 'pascos-pending',
    path: '/pascos/pending',
    element: <ConditionalComponent adminComponent={<PendingApprovalPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Pending Approval',
    protected: true,
    anyPermissions: [PERMISSIONS.PASCO_APPROVE, PERMISSIONS.PASCO_READ],
  },
  {
    id: 'pascos-review',
    path: '/pascos/review',
    element: <ConditionalComponent adminComponent={<PendingReviewPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Pending Review',
    protected: true,
    permission: PERMISSIONS.PASCO_EDIT,
  },
  {
    id: 'pascos-processed',
    path: '/pascos/processed',
    element: <ConditionalComponent adminComponent={<ProcessedPascosPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Processed',
    protected: true,
    permission: PERMISSIONS.PASCO_READ,
  },
  {
    id: 'pascos-edit',
    path: '/pascos/:id/edit',
    element: <ConditionalComponent adminComponent={<EditPascoPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Edit Pasco',
    protected: true,
    permission: PERMISSIONS.PASCO_EDIT,
  },

  // Users & Access Routes (admin only)
  {
    id: 'users',
    path: '/users',
    element: <ConditionalComponent adminComponent={<AllUsersPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'All Admins',
    protected: true,
    permission: PERMISSIONS.USER_READ,
  },
  {
    id: 'users-new',
    path: '/users/new',
    element: <ConditionalComponent adminComponent={<CreateAdminPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Create Admin',
    protected: true,
    permission: PERMISSIONS.USER_CREATE,
  },
  {
    id: 'users-edit',
    path: '/users/:id/edit',
    element: <ConditionalComponent adminComponent={<EditUserPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Edit User',
    protected: true,
    permission: PERMISSIONS.USER_UPDATE,
  },
  {
    id: 'users-detail',
    path: '/users/:id',
    element: <ConditionalComponent adminComponent={<UserDetailPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'User Details',
    protected: true,
    permission: PERMISSIONS.USER_READ,
  },
  // Universities Routes (admin only)
  {
    id: 'universities',
    path: '/universities',
    element: <ConditionalComponent adminComponent={<AllUniversitiesPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'All Universities',
    protected: true,
    permission: PERMISSIONS.UNIVERSITY_READ,
  },
  {
    id: 'universities-new',
    path: '/universities/new',
    element: <ConditionalComponent adminComponent={<CreateUniversityPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Create University',
    protected: true,
    permission: PERMISSIONS.UNIVERSITY_CREATE,
  },
  {
    id: 'universities-edit',
    path: '/universities/:id/edit',
    element: <ConditionalComponent adminComponent={<EditUniversityPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Edit University',
    protected: true,
    permission: PERMISSIONS.UNIVERSITY_UPDATE,
  },
  {
    id: 'universities-detail',
    path: '/universities/:id',
    element: <ConditionalComponent adminComponent={<UniversityDetailPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'University Details',
    protected: true,
    permission: PERMISSIONS.UNIVERSITY_READ,
  },
  // RBAC Routes (admin only)
  {
    id: 'rbac-roles',
    path: '/rbac/roles',
    element: <ConditionalComponent adminComponent={<RolesPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Roles',
    protected: true,
    permission: PERMISSIONS.ROLE_READ,
  },
  {
    id: 'rbac-permissions',
    path: '/rbac/permissions',
    element: <ConditionalComponent adminComponent={<PermissionsPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Permissions',
    protected: true,
    permission: PERMISSIONS.PERMISSION_READ,
  },

  // Universities Routes (admin only)
  {
    id: 'universities',
    path: '/universities',
    element: <ConditionalComponent adminComponent={<AllUniversitiesPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'All Universities',
    protected: true,
    permission: PERMISSIONS.USER_READ, // Using user read permission for now
  },
  {
    id: 'universities-new',
    path: '/universities/new',
    element: <ConditionalComponent adminComponent={<CreateUniversityPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Create University',
    protected: true,
    permission: PERMISSIONS.USER_CREATE, // Using user create permission for now
  },
  {
    id: 'universities-detail',
    path: '/universities/:id',
    element: <ConditionalComponent adminComponent={<UniversityDetailPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'University Details',
    protected: true,
    permission: PERMISSIONS.USER_READ, // Using user read permission for now
  },
  {
    id: 'universities-edit',
    path: '/universities/:id/edit',
    element: <ConditionalComponent adminComponent={<EditUniversityPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Edit University',
    protected: true,
    permission: PERMISSIONS.USER_UPDATE, // Using user update permission for now
  },

  // Background Tasks Routes (admin only)
  {
    id: 'tasks',
    path: '/tasks',
    element: <ConditionalComponent adminComponent={<AllTasksPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'All Tasks',
    protected: true,
    permission: PERMISSIONS.TASK_READ,
  },
  {
    id: 'tasks-failed',
    path: '/tasks/failed',
    element: <ConditionalComponent adminComponent={<FailedTasksPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Failed Tasks',
    protected: true,
    permission: PERMISSIONS.TASK_READ,
  },
  {
    id: 'tasks-detail',
    path: '/tasks/:id',
    element: <ConditionalComponent adminComponent={<TaskDetailPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Task Details',
    protected: true,
    permission: PERMISSIONS.TASK_READ,
  },

  // System Routes (admin only)
  {
    id: 'system-health',
    path: '/system/health',
    element: <ConditionalComponent adminComponent={<SystemHealthPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'System Health',
    protected: true,
    // Available to all authenticated users - system health is important
  },
  {
    id: 'configurations',
    path: '/configurations',
    element: <ConditionalComponent adminComponent={<ConfigurationsPage />} publicComponent={<PublicHomePageRevamped />} />,
    label: 'Configurations',
    protected: true,
    // Admin-only - requires wildcard or specific config permission (not in current list)
  },

  // ==================== SHARED AUTHENTICATED ROUTES ====================

  // Common authenticated routes (shared between public users and admins)
  {
    id: 'profile',
    path: '/profile',
    element: <ProfilePage />,
    label: 'My Profile',
    protected: true,
    // No permission needed - users can view their own profile
  },
  // {
  //   id: 'account-settings',
  //   path: '/account/settings',
  //   element: <AccountSettingsPage />,
  //   label: 'Account Settings',
  //   protected: true,
  //   // No permission needed - users can manage their own settings
  // },
  {
    id: 'security',
    path: '/account/security',
    element: <SecurityPage />,
    label: 'Security Settings',
    protected: true,
    // No permission needed - users can manage their own security
  },
  {
    id: 'notifications',
    path: '/notifications',
    element: <NotificationsPage />,
    label: 'Notifications',
    protected: true,
    // No permission needed - users can view their own notifications
  },
  {
    id: 'help',
    path: '/help',
    element: <HelpCenterPage />,
    label: 'Help Center',
    protected: false,
    isPublic: true, // Available to everyone, uses PublicLayout
  },

  // WhatsApp Linking Page - For users redirected from WhatsApp or needing to link accounts
  {
    id: 'link-whatsapp',
    path: '/link-whatsapp',
    element: <WhatsAppLinkPage />,
    label: 'Link WhatsApp',
    protected: false,
    isPublic: true, // Available to everyone, handles auth state internally
  },



  // =======================================================
];
